import { ReactNode } from 'react'

import { getIsNativeToken, getWrappedToken } from '@cowprotocol/common-utils'
import { isEvmChain } from '@cowprotocol/cow-sdk'
import { BridgeProviderQuoteError, BridgeQuoteErrors } from '@cowprotocol/sdk-bridging'
import { CenteredDots, HelpTooltip, InfoTooltip, TokenSymbol } from '@cowprotocol/ui'

import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import styled from 'styled-components/macro'

import { TradeApproveButton } from 'modules/erc20Approve'
import { CompatibilityIssuesWarning } from 'modules/trade'

import { QuoteApiError, QuoteApiErrorCodes } from 'api/cowProtocol/errors/QuoteError'
import { TradeLoadingButton } from 'common/pure/TradeLoadingButton'

import { ProxyAccountLoading, ProxyAccountUnknown } from './common'

import { XSTOCK_MIN_TRADE_SIZE_USD } from '../../consts'
import { TradeFormButtonContext, TradeFormValidation } from '../../types'
import { TradeFormBlankButton } from '../TradeFormBlankButton'

type ButtonComponent = React.ComponentType<ButtonComponentProps>

type ButtonComponentProps = TradeFormButtonContext & { isDisabled?: boolean }

interface ButtonErrorConfig {
  text: ReactNode
  id?: string
}

function getBridgeQuoteErrorTexts(): Record<BridgeQuoteErrors, string> {
  const DEFAULT_QUOTE_ERROR = getDefaultQuoteError()

  return {
    [BridgeQuoteErrors.API_ERROR]: DEFAULT_QUOTE_ERROR,
    [BridgeQuoteErrors.INVALID_BRIDGE]: DEFAULT_QUOTE_ERROR,
    [BridgeQuoteErrors.TX_BUILD_ERROR]: DEFAULT_QUOTE_ERROR,
    [BridgeQuoteErrors.QUOTE_ERROR]: DEFAULT_QUOTE_ERROR,
    [BridgeQuoteErrors.INVALID_API_JSON_RESPONSE]: DEFAULT_QUOTE_ERROR,
    [BridgeQuoteErrors.NO_INTERMEDIATE_TOKENS]: t`No routes found`,
    [BridgeQuoteErrors.NO_ROUTES]: t`No routes found`,
    [BridgeQuoteErrors.ONLY_SELL_ORDER_SUPPORTED]: t`Only "sell" orders are supported`,
    [BridgeQuoteErrors.QUOTE_DOES_NOT_MATCH_DEPOSIT_ADDRESS]: t`Bridging deposit address is not verified! Please contact CoW Swap support!`,
    [BridgeQuoteErrors.SELL_AMOUNT_TOO_SMALL]: t`Sell amount too small to bridge`,
  }
}

function getDefaultQuoteError(): string {
  return t`Error loading price. Try again later.`
}

function getQuoteErrorTexts(): Record<QuoteApiErrorCodes, string> {
  return {
    [QuoteApiErrorCodes.UNHANDLED_ERROR]: getDefaultQuoteError(),
    [QuoteApiErrorCodes.TransferEthToContract]: t`Buying native currency with smart contract wallets is not currently supported`,
    [QuoteApiErrorCodes.UnsupportedToken]: t`Unsupported token`,
    [QuoteApiErrorCodes.InsufficientLiquidity]: t`Insufficient liquidity for this trade.`,
    [QuoteApiErrorCodes.FeeExceedsFrom]: t`Sell amount is too small`,
    [QuoteApiErrorCodes.ZeroPrice]: t`Invalid price. Try increasing input/output amount.`,
    [QuoteApiErrorCodes.SameBuyAndSellToken]: t`Tokens must be different`,
  }
}

const CompatibilityIssuesWarningWrapper = styled.div`
  margin-top: -10px;
`

const UnsupportedTokenButton = ({ derivedState, isSupportedWallet }: ButtonComponentProps): ReactNode => {
  const { inputCurrency, outputCurrency } = derivedState

  return inputCurrency && outputCurrency ? (
    <>
      <TradeFormBlankButton disabled={true}>
        <Trans>Unsupported token</Trans>
      </TradeFormBlankButton>
      <CompatibilityIssuesWarningWrapper>
        <CompatibilityIssuesWarning
          currencyIn={inputCurrency}
          currencyOut={outputCurrency}
          isSupportedWallet={isSupportedWallet}
        />
      </CompatibilityIssuesWarningWrapper>
    </>
  ) : null
}

export const tradeButtonsMap: Record<TradeFormValidation, ButtonErrorConfig | ButtonComponent> = {
  [TradeFormValidation.WrapUnwrapFlow]: (props: ButtonComponentProps) => {
    const isNativeIn = !!props.derivedState.inputCurrency && getIsNativeToken(props.derivedState.inputCurrency)

    return (
      <TradeFormBlankButton onClick={() => props.wrapNativeFlow()}>
        {isNativeIn ? t`Wrap` : t`Unwrap`}
      </TradeFormBlankButton>
    )
  },
  [TradeFormValidation.CustomTokenError]: ({ customTokenError }: ButtonComponentProps) => {
    return (
      <TradeFormBlankButton disabled={true}>
        <span>{customTokenError}</span>
      </TradeFormBlankButton>
    )
  },
  [TradeFormValidation.CurrencyNotSet]: {
    text: <Trans>Select a token</Trans>,
  },
  [TradeFormValidation.InputAmountNotSet]: {
    text: <Trans>Enter an amount</Trans>,
  },
  [TradeFormValidation.XstockMinimumTradeSize]: {
    text: <Trans>Minimum trade size for xStocks tokens is ${XSTOCK_MIN_TRADE_SIZE_USD}</Trans>,
  },
  [TradeFormValidation.BrowserOffline]: {
    text: <Trans>Error loading price. You are currently offline.</Trans>,
  },
  [TradeFormValidation.RecipientNotSet]: {
    text: <Trans>Enter a receiver address</Trans>,
  },
  [TradeFormValidation.RecipientInvalid]: ({
    derivedState: { inputCurrency, outputCurrency, recipient },
  }: ButtonComponentProps) => {
    const isBridging = inputCurrency && outputCurrency && inputCurrency.chainId !== outputCurrency.chainId
    const isNonEvmBridging = isBridging && outputCurrency && !isEvmChain(outputCurrency.chainId)
    const showEnsTooltip = isBridging && recipient && !isNonEvmBridging

    return (
      <TradeFormBlankButton disabled>
        <>
          <Trans>Enter a valid recipient</Trans>
          {showEnsTooltip && (
            <HelpTooltip
              placement="top"
              text={t`ENS recipient not supported for Swap and Bridge. Use address instead.`}
            />
          )}
        </>
      </TradeFormBlankButton>
    )
  },
  [TradeFormValidation.CurrencyNotSupported]: (props: ButtonComponentProps) => {
    return <UnsupportedTokenButton {...props} />
  },
  [TradeFormValidation.QuoteErrors]: (props: ButtonComponentProps) => {
    const DEFAULT_QUOTE_ERROR = t`Error loading price. Try again later.`

    const quoteErrorTexts = getQuoteErrorTexts()

    {
      /*TODO: sell=buy feature. Remove all SameBuyAndSellToken usages once feature is ready */
    }
    const quoteErrorTextsForBridges: Partial<Record<QuoteApiErrorCodes, string>> = {
      [QuoteApiErrorCodes.SameBuyAndSellToken]: t`Not yet supported`,
    }

    const bridgeQuoteErrorTexts = getBridgeQuoteErrorTexts()

    const errorTooltipContentForBridges: Partial<Record<QuoteApiErrorCodes, string>> = {
      [QuoteApiErrorCodes.SameBuyAndSellToken]: t`Bridging without swapping is not yet supported. Let us know if you want this feature!`,
    }

    const { quote } = props

    if (quote.error instanceof QuoteApiError) {
      const errorType = quote.error.type

      if (errorType === QuoteApiErrorCodes.UnsupportedToken) {
        return <UnsupportedTokenButton {...props} />
      }

      const isBridge = quote.isBridgeQuote
      const errorText = (() => {
        const quoteErrorText = quoteErrorTexts[errorType]
        const bridgeQuoteErrorText = quoteErrorTextsForBridges[errorType]

        if (isBridge && bridgeQuoteErrorText) {
          // Do not display "Not yet supported" when sell and intermediate tokens are the same
          // Because user doesn't see intermediate token
          if (errorType === QuoteApiErrorCodes.SameBuyAndSellToken) {
            const areSwapAssetsDifferent =
              props.derivedState.inputCurrency?.symbol?.toLowerCase() !==
              props.derivedState.outputCurrency?.symbol?.toLowerCase()

            if (areSwapAssetsDifferent) {
              return bridgeQuoteErrorTexts[BridgeQuoteErrors.NO_ROUTES]
            }
          }

          return bridgeQuoteErrorText
        }

        return quoteErrorText || DEFAULT_QUOTE_ERROR
      })()

      const errorTooltipText = isBridge && errorTooltipContentForBridges[errorType]

      return (
        <TradeFormBlankButton disabled={true}>
          <>
            {errorText}
            {errorTooltipText && <HelpTooltip text={errorTooltipText} />}
          </>
        </TradeFormBlankButton>
      )
    }

    if (quote.error instanceof BridgeProviderQuoteError) {
      const errorMessage = quote.error.message as BridgeQuoteErrors
      const errorText = bridgeQuoteErrorTexts[errorMessage] || DEFAULT_QUOTE_ERROR

      return (
        <TradeFormBlankButton disabled={true}>
          <>
            {errorText}
            {errorMessage === BridgeQuoteErrors.NO_INTERMEDIATE_TOKENS && (
              <InfoTooltip content={t`No intermediate tokens found for the route`} />
            )}
          </>
        </TradeFormBlankButton>
      )
    }

    return (
      <TradeFormBlankButton disabled={true}>
        <Trans>Unknown quote error</Trans>
      </TradeFormBlankButton>
    )
  },
  [TradeFormValidation.QuoteExpired]: {
    text: <Trans>Quote expired. Refreshing...</Trans>,
  },
  [TradeFormValidation.WalletNotConnected]: (props: ButtonComponentProps) => {
    return (
      <TradeFormBlankButton onClick={props.connectWallet} disabled={props.widgetStandaloneMode === false}>
        <Trans>Connect Wallet</Trans>
      </TradeFormBlankButton>
    )
  },
  [TradeFormValidation.WalletNotSupported]: {
    text: <Trans>Wallet Unsupported</Trans>,
  },
  [TradeFormValidation.NetworkNotSupported]: {
    text: <Trans>Unsupported Network</Trans>,
  },
  [TradeFormValidation.NetworkDeprecated]: {
    text: <Trans>Deprecated Network</Trans>,
  },
  [TradeFormValidation.SafeReadonlyUser]: {
    text: (
      <>
        <span>
          <Trans>Connect signer</Trans>
        </span>{' '}
        <HelpTooltip
          text={
            <div>
              <Trans>
                Your Safe is not connected with a signer.
                <br />
                To place an order, you must connect using a signer of this Safe.
              </Trans>
            </div>
          }
        />
      </>
    ),
  },
  [TradeFormValidation.WalletCapabilitiesLoading]: {
    text: (
      <>
        <CenteredDots smaller />
      </>
    ),
  },
  [TradeFormValidation.QuoteLoading]: {
    text: <TradeLoadingButton />,
  },
  [TradeFormValidation.ImpactLoading]: {
    text: (
      <>
        <Trans>Fetching price impact</Trans>
        <CenteredDots smaller />
      </>
    ),
  },
  [TradeFormValidation.BalancesLoading]: {
    text: (
      <>
        <Trans>Fetching balances</Trans>
        <CenteredDots smaller />
      </>
    ),
  },
  [TradeFormValidation.BalancesNotLoaded]: (props: ButtonComponentProps) => {
    let errorMessage: string | undefined = undefined

    if (props.balancesError?.includes('rate limit')) {
      errorMessage = t`Request is being rate limited`
    }

    return (
      <TradeFormBlankButton disabled={true}>
        <>
          <Trans>Couldn't load balances</Trans>
          {errorMessage ? <HelpTooltip text={<div>{errorMessage}</div>} /> : null}
        </>
      </TradeFormBlankButton>
    )
  },
  [TradeFormValidation.BalanceInsufficient]: (props: ButtonComponentProps) => {
    const inputCurrency = props.derivedState.inputCurrency

    return (
      <TradeFormBlankButton disabled={true}>
        <span>
          <Trans>Insufficient {<TokenSymbol token={inputCurrency} />} balance</Trans>
        </span>
      </TradeFormBlankButton>
    )
  },
  [TradeFormValidation.ApproveAndSwapInBundle]: ({ isDisabled = false, ...context }: ButtonComponentProps) => {
    const inputCurrency = context.derivedState.inputCurrency
    const tokenToApprove = inputCurrency && getWrappedToken(inputCurrency)
    const contextDefaultText = context.defaultText

    return (
      <TradeFormBlankButton disabled={isDisabled} onClick={context.confirmTrade} clickEvent={context.approveClickEvent}>
        <span>
          <Trans>
            Approve {<TokenSymbol token={tokenToApprove} length={6} />} and {contextDefaultText}
          </Trans>
        </span>
      </TradeFormBlankButton>
    )
  },
  [TradeFormValidation.ApproveRequired]: ({ isDisabled = false, ...context }: ButtonComponentProps) => {
    const { amountToApprove, supportsPartialApprove, defaultText } = context
    if (!amountToApprove) return null

    return (
      <TradeApproveButton
        isDisabled={isDisabled}
        amountToApprove={amountToApprove}
        supportsPartialApprove={supportsPartialApprove}
        onApproveConfirm={context.confirmTrade}
        minAmountToSignForSwap={context.minAmountToSignForSwap}
        approveClickEvent={context.approveClickEvent}
        swapClickEvent={context.confirmClickEvent}
      >
        <TradeFormBlankButton disabled={!supportsPartialApprove}>{defaultText}</TradeFormBlankButton>
      </TradeApproveButton>
    )
  },
  [TradeFormValidation.SellNativeToken]: (context) => {
    const inputCurrency = context.derivedState.inputCurrency
    const isNativeIn = !!inputCurrency && getIsNativeToken(inputCurrency)
    const symbol = inputCurrency?.symbol

    if (!isNativeIn) return null

    return (
      <TradeFormBlankButton disabled>
        <Trans>Selling {symbol} is not supported</Trans>
      </TradeFormBlankButton>
    )
  },
  [TradeFormValidation.ImportingIntermediateToken]: {
    text: <Trans>Import intermediate token</Trans>,
  },
  [TradeFormValidation.ProxyAccountLoading]: {
    text: <ProxyAccountLoading />,
  },
  [TradeFormValidation.ProxyAccountUnknown]: {
    text: <ProxyAccountUnknown />,
  },
  [TradeFormValidation.RestrictedForCountry]: {
    text: <Trans>This token is not available in your region</Trans>,
  },
  [TradeFormValidation.DisableTradeWithUnknownPriceImpact]: () => {
    return (
      <TradeFormBlankButton disabled>
        <>
          <Trans>Unknown price impact</Trans>
          <HelpTooltip
            placement="top"
            text={t`Not enough price data for one or both assets to calculate the price impact`}
          />
        </>
      </TradeFormBlankButton>
    )
  },
  [TradeFormValidation.DisableTradeWithHighPriceImpact]: ({ widgetPriceImpactThreshold = 0 }) => {
    return (
      <TradeFormBlankButton disabled>
        <>
          <Trans>Price impact is too high</Trans>
          <HelpTooltip
            placement="top"
            text={t`Trading is not allowed with price impact higher than ${widgetPriceImpactThreshold}%`}
          />
        </>
      </TradeFormBlankButton>
    )
  },
}
